import { useEffect, useRef, useState } from 'react';

interface NameDialogProps {
  open: boolean;
  title: string; // dialog heading, e.g. "New dashboard" or "Rename dashboard"
  initialValue: string; // prefilled name
  confirmLabel: string; // e.g. "Create" or "Rename"
  onConfirm: (name: string) => void;
  onCancel: () => void; // dismiss (backdrop/Escape/Cancel) — no-op on data
}

/**
 * Small modal for naming a dashboard on create, or renaming the current one.
 * Dismissing (backdrop click, Escape, or Cancel) is a no-op on data — only the
 * confirm button commits, passing the trimmed value to onConfirm. The confirm
 * button is disabled while the trimmed value is empty.
 */
export function NameDialog({
  open,
  title,
  initialValue,
  confirmLabel,
  onConfirm,
  onCancel,
}: NameDialogProps) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Re-seed the field each time the dialog opens (the same instance is reused
  // across create/rename invocations with different initialValue).
  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  // Autofocus + select the prefilled value on open.
  useEffect(() => {
    if (!open) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [open]);

  // Escape cancels.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const trimmed = value.trim();
  const submit = () => {
    if (trimmed) onConfirm(trimmed);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="name-dialog-title"
        className="relative z-10 mx-4 w-full max-w-md"
      >
        <div className="card border-2 border-kinetica-600/30">
          <h3 id="name-dialog-title" className="mb-4 text-base font-semibold text-white">
            {title}
          </h3>

          <input
            ref={inputRef}
            aria-label="Dashboard name"
            className="input w-full text-sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />

          <div className="mt-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary px-4 py-2 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!trimmed}
              className="btn-primary px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
